import { Checkbox } from '@ardoq/forms';
import styled, { css } from 'styled-components';
import { MultiLabelFormattingProps } from '../../types';
import { caption, text1 } from '@ardoq/typography';
import { ButtonWithDropdown } from '@ardoq/dropdown-menu';
import { LabelFormatting } from './LabelFormatting';
import { spacing } from '@ardoq/design-tokens';
import { FlexBox, Stack } from '@ardoq/layout';
import { AddIcon } from '@ardoq/icons';
import { TabNotification } from '@ardoq/perspectives';
import { type I18nLocale, t } from '@ardoq/i18n';
import { AiButton } from '@ardoq/button';

const ShowReferenceTypeContainer = styled.div<{
  $isNarrow?: boolean;
}>`
  display: flex;
  align-items: center;
  ${({ $isNarrow }) =>
    !$isNarrow &&
    css`
      margin-bottom: ${spacing.s8};
    `}
`;
const Label = styled.label`
  ${text1}
`;
const SmallLabel = styled.label`
  ${caption}
`;

type LabelFormattingProps = MultiLabelFormattingProps & {
  isHorizontal?: boolean;
  i18nLocale?: I18nLocale;
};

const PerspectivesLabelFormattingWithMultiselect = ({
  labelFormatting = [],
  showReferenceType,
  onLabelFormattingReordered,
  onLabelFormattingDeleted,
  onUpdateLabelFormattingAt,
  onShowReferenceTypeToggled,
  labelFormattingElementProps,
  addLabelButtonProps,
  disableReferenceFormattingMessage,
  restrictedMultiLabelFormattingWarningProps,
  isAiButtonVisible,
  onAiButtonClicked,
  isHorizontal,
  i18nLocale = {},
}: LabelFormattingProps) => (
  <>
    {!isHorizontal && (
      <Label>
        {t(
          i18nLocale,
          'perspectivesSidebar:labels.showFieldValuesDescription',
          'Show field values on specific component and reference types. Component name is shown by default'
        )}
      </Label>
    )}
    <ShowReferenceTypeContainer $isNarrow={!isHorizontal}>
      <Checkbox
        name="showReferenceType"
        isChecked={showReferenceType}
        onChange={onShowReferenceTypeToggled}
        isDisabled={Boolean(disableReferenceFormattingMessage)}
        popoverContent={disableReferenceFormattingMessage}
      >
        {t(
          i18nLocale,
          'perspectivesSidebar:labels.showReferenceType',
          'Show reference type'
        )}
      </Checkbox>
    </ShowReferenceTypeContainer>
    <TabNotification
      notificationData={restrictedMultiLabelFormattingWarningProps}
    />
    <Stack gap={isHorizontal ? 'medium' : 'small'}>
      <LabelFormatting
        labelFormatting={labelFormatting}
        labelFormattingElementProps={labelFormattingElementProps}
        onLabelFormattingDeleted={onLabelFormattingDeleted}
        onLabelFormattingReordered={onLabelFormattingReordered}
        onUpdateLabelFormattingAt={onUpdateLabelFormattingAt}
        isHorizontal={isHorizontal}
      />
      <FlexBox gap="small" flexWrap>
        <ButtonWithDropdown {...addLabelButtonProps}>
          <AddIcon />
          {t(i18nLocale, 'perspectivesSidebar:labels.addLabel', 'Add label')}
        </ButtonWithDropdown>
        {isAiButtonVisible && onAiButtonClicked && (
          <AiButton i18nLocale={i18nLocale} onClick={onAiButtonClicked}>
            {t(
              i18nLocale,
              'perspectivesSidebar:aiButton.labelFormatting.text',
              'Label with AI'
            )}
          </AiButton>
        )}
      </FlexBox>
      <SmallLabel>
        {t(
          i18nLocale,
          'perspectivesSidebar:labels.labelsOrder',
          'Labels will appear in the order selected here'
        )}
      </SmallLabel>
    </Stack>
  </>
);

export { PerspectivesLabelFormattingWithMultiselect };
